import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Input, Spin } from 'antd';
import { LoadingOutlined, LockOutlined, SafetyCertificateOutlined, UserOutlined } from '@ant-design/icons';
import './PasswordLogin.less';
import { getCaptcha, login } from '@/services/login';
import LoginEvent, { LoginSuccess } from '@/events/login-event';
import { finalize } from 'rxjs';

interface Captcha {
  captchaId: string;
  captchaImage: string;
  captchaValue: string;
}

const PasswordLogin = () => {
  const [captcha, setCaptcha] = useState<Captcha>({
    captchaId: '',
    captchaImage: '',
    captchaValue: '',
  });

  const [isSubmit, setSubmit] = useState<boolean>(false);
  const [isCaptchaLoading, setCaptchaLoading] = useState(true);
  const autoRefreshCaptchaTimer = useRef<number>();
  const [form] = Form.useForm();

  const onFinish = (values: any) => {
    setSubmit(true);
    login({
      username: values.username,
      password: values.password,
      captchaId: captcha.captchaId,
      captchaValue: values.captcha,
    }).pipe(finalize(() => {
      setSubmit(false);
    })).subscribe({
      next() {
        LoginEvent.emit(LoginSuccess);
      },
      error() {
        loadCaptcha();
      },
    });
  };

  const loadCaptcha = useCallback(() => {
    clearTimeout(autoRefreshCaptchaTimer.current);
    autoRefreshCaptchaTimer.current = setTimeout(() => {
      loadCaptcha();
    }, 90000);
    setCaptchaLoading(true);
    form.setFieldsValue({ captcha: '' });
    getCaptcha()
      .subscribe((data: any) => {
        setCaptcha(data as Captcha);
        setCaptchaLoading(false);
      });
  }, [form]);

  useEffect(() => {
    loadCaptcha();

    return () => {
      clearTimeout(autoRefreshCaptchaTimer.current);
    };
  }, []);

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin/>;

  return (
    <React.Fragment>
      <div className="app-area">
        <div className="app-box">
          <div className="app-box-content">
            <div className="app-page">
              <div className="app-content">
                <div className="app-info">
                  <div className="app-icon">
                    {/* <img src={loginPageVars.clientInfo.icon} alt=""/> */}
                  </div>
                  <div className="login-title">登录</div>
                  <div className="login-desc">管理后台</div>
                </div>
                <div className="login-split-25"></div>
                <Form
                  name="normal_login"
                  className="login-form"
                  onFinish={onFinish}
                  form={form}
                >
                  <Form.Item
                    name="username"
                    rules={[{ required: true, message: '请输入用户名!' }]}
                  >
                    <Input prefix={<UserOutlined className="site-form-item-icon"/>} placeholder="用户名" size="large"/>
                  </Form.Item>
                  <Form.Item
                    name="password"
                    rules={[{ required: true, message: '请输入密码!' }]}
                  >
                    <Input.Password
                      prefix={<LockOutlined className="site-form-item-icon"/>}
                      type="password"
                      placeholder="密码"
                      size="large"
                      allowClear
                    />
                  </Form.Item>
                  <Form.Item>
                    <div className="captcha-item">
                      <div className="captcha-input">
                        <Form.Item
                          name="captcha"
                          noStyle
                          rules={[{ required: true, message: '请输入验证码!' }]}
                        >
                          <Input
                            prefix={<SafetyCertificateOutlined className="site-form-item-icon"/>}
                            placeholder="验证码"
                            size="large"
                            allowClear
                          />
                        </Form.Item>
                      </div>
                      <div className="captcha-img" onClick={() => {
                        loadCaptcha();
                      }}>
                        {
                          isCaptchaLoading
                            ? <Spin indicator={antIcon}/>
                            : <img src={captcha.captchaImage} alt="验证码图片" title="点击更换验证码图片"/>
                        }
                      </div>
                    </div>
                  </Form.Item>
                  <Form.Item>
                    <Button type="primary"
                            size="large"
                            block
                            loading={isSubmit}
                            htmlType="submit"
                            className="login-form-button">
                      登录
                    </Button>
                  </Form.Item>
                </Form>
              </div>
              <div className="module-footer"></div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default PasswordLogin;
